// The customer's desired start ("hvornår skal opgaven løses?") — asked in every
// wizard and shown to the partner with the delivered task, so a partner never
// proposes a week the customer has ruled out.
//
// Pure data + pure functions, no Prisma client import — safe from both server
// code and "use client" components.

import type { LeadStartTiming } from "@/generated/prisma/enums";
import { formatDate, formatDateInput } from "@/shared/format/date";

// Order is the order the wizards render them in: soonest first, "fleksibel"
// last so it reads as the opt-out rather than the default.
export const START_TIMING_OPTIONS: { value: LeadStartTiming; label: string }[] = [
  { value: "HURTIGST_MULIGT", label: "Hurtigst muligt" },
  { value: "INDEN_14_DAGE", label: "Inden 14 dage" },
  { value: "INDEN_1_MAANED", label: "Inden en måned" },
  { value: "SPECIFIK_DATO", label: "En bestemt dato" },
  { value: "FLEKSIBEL", label: "Fleksibel" },
];

const START_TIMING_LABELS: Record<LeadStartTiming, string> = {
  HURTIGST_MULIGT: "Hurtigst muligt",
  INDEN_14_DAGE: "Inden 14 dage",
  INDEN_1_MAANED: "Inden en måned",
  SPECIFIK_DATO: "En bestemt dato",
  FLEKSIBEL: "Fleksibel",
};

// How many days ahead each bucket's window reaches. SPECIFIK_DATO is absent —
// its window comes from the date itself.
const BUCKET_WINDOW_DAYS: Record<Exclude<LeadStartTiming, "SPECIFIK_DATO">, number> = {
  HURTIGST_MULIGT: 7,
  INDEN_14_DAGE: 14,
  INDEN_1_MAANED: 30,
  FLEKSIBEL: 30,
};

export function isStartTiming(value: unknown): value is LeadStartTiming {
  return typeof value === "string" && value in START_TIMING_LABELS;
}

// A wish further out than this is noise: the customer's plans will have changed
// before any partner could act on it. Bounds the date input in the wizard and
// the same value is re-checked server-side.
const MAX_START_MONTHS_AHEAD = 12;

// min/max for the wizards' <input type="date">.
export function startDateInputBounds(): { min: string; max: string } {
  const now = new Date();
  const max = new Date(now);
  max.setMonth(max.getMonth() + MAX_START_MONTHS_AHEAD);
  return { min: formatDateInput(now), max: formatDateInput(max) };
}

// Parses a yyyy-mm-dd wish into UTC midnight, rejecting anything outside the
// window the wizard allowed. Yesterday is accepted so a submit that crosses
// midnight (or a client clock an hour off) doesn't throw away a valid answer.
export function parseStartDateInput(raw: string, now: Date): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const parsed = new Date(`${raw}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return null;

  const earliest = addDays(startOfUtcDay(now), -1);
  const latest = startOfUtcDay(now);
  latest.setUTCMonth(latest.getUTCMonth() + MAX_START_MONTHS_AHEAD);
  if (parsed < earliest || parsed > latest) return null;
  return parsed;
}

function startOfUtcDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

// Human label for the customer's wish. SPECIFIK_DATO resolves to the date
// itself — "1. september 2026" says more to a partner than "En bestemt dato".
export function humanizeStartTiming(timing: LeadStartTiming, startDate: Date | null): string {
  if (timing === "SPECIFIK_DATO") {
    return startDate ? formatDate(startDate) : START_TIMING_LABELS.SPECIFIK_DATO;
  }
  return START_TIMING_LABELS[timing];
}

function addDays(base: Date, days: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
}

// The concrete date window behind a start bucket, so the partner sees what the
// customer asked for instead of a fixed "next week".
//
// Measured from `now` rather than the lead's createdAt: `now` is the only
// reference under which the earliest date can't land in the past.
export function startTimingWindow(args: {
  timing: LeadStartTiming;
  startDate: Date | null;
  now: Date;
}): { earliest: Date; latest: Date } {
  const { timing, startDate, now } = args;
  if (timing === "SPECIFIK_DATO" && startDate) {
    const target = new Date(Math.max(startDate.getTime(), now.getTime()));
    return { earliest: target, latest: target };
  }
  // SPECIFIK_DATO without a date shouldn't be reachable (the route rejects it),
  // but it still has to yield a window — treat it as the soonest bucket.
  const days =
    timing === "SPECIFIK_DATO" ? BUCKET_WINDOW_DAYS.HURTIGST_MULIGT : BUCKET_WINDOW_DAYS[timing];
  return { earliest: now, latest: addDays(now, days) };
}
